import { useAuthUser } from "../../../contexts/AuthUserContext";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

import CallChart from "../../../components/insights/CallChart";
import DurationChart from "../../../components/insights/DurationChart";
import SuccessChart from "../../../components/insights/SuccessChart";

import { useSentiment } from "../../../contexts/SentimentContext";

import AlertsCard from "../../../components/alerts/alertCard";
import { useAlerts } from "../../../contexts/AlertsContext.jsx";
import { useCallData } from "../../../contexts/CallDataContext.jsx";
import { useWeek } from "../../../contexts/WeekContext";
import { useCustomer } from "../../../contexts/CustomerContext";
import { startOfDay, endOfDay, isWithinInterval } from "date-fns";
import { parseCustomTimestamp } from "../../../utils/dateUtils";

const ScaleHome = () => {
    const user = useAuthUser();
    const { currentCustomer } = useCallData();
    const { getEffectiveCustomerId } = useCustomer();
    const { sentimentCounts } = useSentiment();
    const navigate = useNavigate();
    const { dateRange } = useWeek();

    // console.log('Sentiments', sentimentCounts);

    const { alerts, isLoadingAlerts, fetchError } = useAlerts();

    // Animation states
    const [animationStates, setAnimationStates] = useState({
        header: false,
        insights: false,
        sentiment: false,
        alerts: false
    });

    // Trigger staggered animations on component mount
    useEffect(() => {
        const timeouts = [];

        // Stagger the animations with delays
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, header: true }));
        }, 100));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, insights: true }));
        }, 300));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, sentiment: true }));
        }, 500));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, alerts: true }));
        }, 700));

        // Cleanup timeouts on unmount
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, []);

    // Get assistant name based on client
    const getAssistantName = () => {
        const clientId = getEffectiveCustomerId();
        if (clientId === 'celeste' || clientId === 'helios') {
            return 'Celeste';
        } else if (clientId === 'wards') {
            return 'Randi';
        } else if (clientId === 'scale') {
            return 'BDR';
        } else if (clientId === 'kronk') {
            return 'Kronk';
        } else {
            // For other clients, use their ID in Title Case
            return clientId ? clientId.charAt(0).toUpperCase() + clientId.slice(1).toLowerCase() : 'Assistant';
        }
    };

    // Get assistant phone number based on client
    const getAssistantPhoneNumber = () => {
        const clientId = getEffectiveCustomerId();
        if (clientId === 'wards') {
            return '+12489709365';
        }
        return null; // N/A for celeste/helios and all other clients
    };

    // Add this function to filter calls by date range
    const getFilteredCalls = () => {
        if (!currentCustomer || !dateRange?.start || !dateRange?.end) return [];
        
        const rangeStart = startOfDay(dateRange.start);
        const rangeEnd = endOfDay(dateRange.end);
        
        return currentCustomer.filter(call => {
            const callDate = parseCustomTimestamp(call?.metadata?.timestamp);
            if (!callDate) return false;
            return isWithinInterval(callDate, { start: rangeStart, end: rangeEnd });
        });
    };

    // Use this function where you need filtered call counts
    const filteredCalls = getFilteredCalls();
    const totalFilteredCalls = filteredCalls.length;

    return (
        <div className="responsive-page-layout scrollable">
            <div className={`home-title dashboard-header-responsive transition-all duration-700 ease-out ${
                animationStates.header
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
            }`}>
                <div className="flex flex-col gap-2 items-start w-full lg:w-auto">
                    <h4 className="text-xl sm:text-2xl flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                        <div className="flex flex-row gap-2 items-center">
                            <Icon icon="mdi:hand-wave" className="text-primary text-2xl sm:text-3xl"></Icon>
                            Hello <span className="text-primary">{user.name}</span>
                        </div>
                    </h4>
                    <p className="text-font-secondary text-sm sm:text-base lg:text-lg">Here's a quick snapshot of your AI assistant's performance this week.</p>
                </div>
                <div className="header-right-3">
                    <div className='flex flex-col sm:flex-row gap-2 w-full lg:w-fit'>
                        <div className="flex flex-row gap-1 w-fit items-center text-xs sm:text-sm p-2 rounded-md bg-bgSecondary shadow-neo-card">
                            <Icon icon="mingcute:ai-fill" className="text-lg sm:text-xl text-accent"></Icon>
                            <p><strong>AI Assistant:</strong> {getAssistantName()}</p>
                        </div>
                        {getAssistantPhoneNumber() && (
                            <div className={`
                                flex flex-row gap-1 items-center w-fit text-xs sm:text-sm p-2 rounded-md bg-bgSecondary shadow-neo-card
                                cursor-pointer hover:bg-neo-button transition-colors`}
                                onClick={() => window.location.href = `tel:${getAssistantPhoneNumber()}`}
                            >
                                <Icon icon="material-symbols:call" className="text-lg sm:text-xl text-accent"></Icon>
                                <p className="hidden sm:inline"><strong>Assistant Phone Number:</strong> +1 (248) 970 9365</p>
                                <p className="sm:hidden"><strong>Phone:</strong> +1 (248) 970 9365</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Key Insights */}
            <div className={`home-insights w-full flex flex-col gap-4 transition-all duration-1000 ease-out ${
                animationStates.insights
                    ? 'opacity-100'
                    : 'opacity-0'
            }`}>
                <div className="home-insights-title responsive-flex-left">
                    <div className="flex flex-row w-fit gap-1 items-center text-lg sm:text-xl">
                        <Icon icon="system-uicons:graph-bar" className="text-secondary text-xl sm:text-2xl"></Icon>
                        Key Insights
                    </div>
                    <Link to={`/app/scale/insights`} id="key-insights-button" className="flex flex-row items-center gap-1 text-accent text-sm sm:text-base">
                        View all <Icon icon="mingcute:right-line" className="text-lg sm:text-xl"></Icon>
                    </Link>
                </div>
                <div className="dashboard-insights-responsive">
                    <div className="chart-container-responsive">
                        <CallChart />
                    </div>
                    <div className="chart-container-responsive">
                        <DurationChart />
                    </div>
                    <div className="chart-container-responsive">
                        <SuccessChart />
                    </div>
                </div>
            </div>
            {/* Call Sentiment Summary */}
            <div className={`home-insights w-full flex flex-col gap-4 transition-all duration-1000 ease-out ${
                animationStates.sentiment
                    ? 'opacity-100'
                    : 'opacity-0'
            }`}>
                <div className="flex flex-row w-fit gap-1 items-center text-lg sm:text-xl">
                    <Icon icon="fluent:person-call-24-regular" className="text-secondary text-xl sm:text-2xl"></Icon>
                    Call Sentiment Summary
                </div>
                <div className="dashboard-sentiment-responsive">
                    <div className="bg-bgSecondary shadow-neo-card-inner flex-1 p-3 sm:p-4 rounded-md flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <Icon icon="icon-park-outline:positive-dynamics" className="text-xl sm:text-2xl text-green-500" />
                            <span className="text-sm sm:text-base">Positive</span>
                        </div>
                        <span className="text-green-500 font-bold text-lg sm:text-xl">{sentimentCounts.improving}</span>
                    </div>
                    <div className="bg-bgSecondary shadow-neo-card-inner flex-1 p-3 sm:p-4 rounded-md flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <Icon icon="icon-park-outline:dividing-line-one" className="text-xl sm:text-2xl text-yellow-400" />
                            <span className="text-sm sm:text-base">Stable</span>
                        </div>
                        <span className="text-yellow-400 font-bold text-lg sm:text-xl">{sentimentCounts.stable}</span>
                    </div>
                    <div className="bg-bgSecondary shadow-neo-card-inner flex-1 p-3 sm:p-4 rounded-md flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <Icon icon="icon-park-outline:negative-dynamics" className="text-xl sm:text-2xl text-error" />
                            <span className="text-sm sm:text-base">Negative</span>
                        </div>
                        <span className="text-error font-bold text-lg sm:text-xl">{sentimentCounts.declining}</span>
                    </div>
                </div>
            </div>
            {/* Priority Alerts */}
            <div className={`home-insights w-full flex flex-col gap-4 transition-all duration-1000 ease-out ${
                animationStates.alerts
                    ? 'opacity-100'
                    : 'opacity-0'
            }`}>
                <div className="flex flex-row w-fit gap-1 items-center text-lg sm:text-xl">
                    <Icon icon="mingcute:alert-line" className="text-secondary text-xl sm:text-2xl"></Icon>
                    Priority Alerts
                </div>
                <div className="flex flex-col items-start w-full space-y-4">
                    <div className="dashboard-alerts-responsive">
                        {isLoadingAlerts && <p className="text-gray-400 col-span-full">Loading alerts...</p> /* Loading indicator */}
                        {fetchError && <p className="text-yellow-500 col-span-full">⚠️ {fetchError}</p> /* Error message */}
                        {!isLoadingAlerts && !fetchError && alerts.length === 0 && <p className="text-gray-400 col-span-full">No alerts found.</p>}
                        {!isLoadingAlerts && !fetchError && alerts
                            .filter(alert => alert.actionRequired === true)
                            .slice(0, 4)
                            .map(alert => (
                                <AlertsCard alert={alert} key={alert.id} onClick={() => navigate('/app/alerts')} />
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScaleHome;